import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import {
  StarRatingQuestionModelTypes,
  StarRatingResponse,
  IStarRatingResponseValidation,
  IStarRatingQuestionSchema
} from './types'

import useSession from '../../session/useSession'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'

import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import StarRatingView from './View'

import { getSmallAssetPath } from '@aytm/lib/common/assets'
import imageAssetWithOptions from '@aytm/app/swidget/base/modules/preview/functions/imageAssetWithOptions'

const StarRatingQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<StarRatingQuestionModelTypes>>(({ model }, ref) => {
    const [[__response, responseValidation, __frame], setResponse] = useState<
      [StarRatingResponse, IStarRatingResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const previewOptions = usePreviewOptions()
    const [showValidation, setShowValidation] = useState(false)

    useEffect(() => {
      session.enableNext(responseValidation.valid)
    }, [session, responseValidation.valid])

    const schema = useMemo(() => {
      const localSchema = JSON.parse(JSON.stringify(model.schema)) as IStarRatingQuestionSchema
      localSchema.matrix_batches.forEach(a => {
        a.image = imageAssetWithOptions(session.rules?.sub?.[localSchema.id]?.[a.id], a.image)
        a.empty = a.hidden || false // se sr mx may have empty batches - when all batches deleted in editor backend add one with property hidden: true
        if (a.empty) a.text = ''

        a.fakeHidden =
          previewOptions.showHidden && false == session.rules?.show?.[a.id] && !previewOptions.suppressLogic
        a.hidden = !previewOptions.showHidden && false == session.rules?.show?.[a.id] // hidden by logic

        return a
      })
      return localSchema
    }, [model.schema, previewOptions, session.rules?.show, session.rules?.sub])

    const onUpdateResponse = (response: StarRatingResponse) => {
      model.response = response
      setResponse([model.response, model.responseValidation, model.frame])
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(
      ref,
      () => {
        return {
          tryNext() {
            if (model.responseValidation.valid || session.skipValidation) {
              return false
            } else {
              session.enableNext(false)
              return true
            }
          },
          tryPrev() {
            return false
          },
          showValidation() {
            setShowValidation(true)
          }
        }
      },
      [model, session]
    )

    const imagesRef = useRef<string[]>(null)
    if (!imagesRef.current)
      imagesRef.current = model.schema.matrix_batches.reduce((array: string[], batch) => {
        if (model.schema.matrix_batches || (batch?.image && !batch.image.expand))
          array.push(getSmallAssetPath(batch.image))
        return array
      }, [])

    return (
      <QuestionFrame model={model} images={imagesRef.current}>
        <StarRatingView
          schema={schema}
          responseValidation={model.responseValidation}
          response={model.response}
          showValidation={showValidation}
          onUpdateResponse={onUpdateResponse}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

StarRatingQuestion.displayName = 'StarRatingQuestion'

export default StarRatingQuestion
